import { z } from 'zod';
import apiClient from '../client';

const normalizeEmail = (email: string) => (email || '').trim().toLowerCase();

const loginSchema = z.object({
  email: z.string().email().transform(normalizeEmail),
  password: z.string().min(1)
});

const registerSchema = z.object({
  email: z.string().email().transform(normalizeEmail),
  password: z.string().min(8),
  fullName: z.string().min(1),
  userName: z.string().min(1),
  // Cloudflare Turnstile token. Optional so the form still submits when the
  // widget is disabled; the backend decides whether it is required.
  captchaToken: z.string().optional()
});

const googleAuthSchema = z.object({
  access_token: z.string()
});

export const auth = {
  login: async (data: z.infer<typeof loginSchema>) => {
    const validated = loginSchema.parse(data);
    const response = await apiClient.post('/auth/login', validated);
    return response.data;
  },

  register: async (data: z.infer<typeof registerSchema>) => {
    const validated = registerSchema.parse(data);
    const response = await apiClient.post('/auth/register', validated);
    return response.data;
  },

  googleAuth: async (data: z.infer<typeof googleAuthSchema>) => {
    const validated = googleAuthSchema.parse(data);
    const response = await apiClient.post('/auth/google', validated);
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await apiClient.post('/auth/forgot-password', { email: normalizeEmail(email) });
    return response.data;
  },

  resetPassword: async (data: { password: string; token: string }) => {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await apiClient.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await apiClient.post('/auth/resend-verification', { email: normalizeEmail(email) });
    return response.data;
  },

  logout: async () => {
    await apiClient.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoleId');
  },

  me: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  }
};